/**
 * Класс для работы с локальным хранилищем с использованием JSON
 */
export class Storage<T> {
	#name: string;

	constructor(name: string) {
		this.#name = name;
	}

	/**
	 * Возвращает объект хранилища
	 */
	#getStoredObject = () => {
		const storedValue = localStorage.getItem(this.#name);
		try {
			return storedValue ? (JSON.parse(storedValue) as Partial<T>) : null;
		} catch (error) {
			return null;
		}
	};

	/**
	 * Изменяет объект хранилища
	 * @param newValue новый объект хранилища
	 */
	#setStoredObject = (newValue: Partial<T>) => {
		if (Object.keys(newValue).length === 0) {
			localStorage.removeItem(this.#name);
		} else {
			localStorage.setItem(this.#name, JSON.stringify(newValue));
		}
	};

	/**
	 * Возвращает значение свойства из хранилища
	 * @param key название свойства объекта хранилища
	 */
	get = <P>(key: keyof T) => {
		const storedObject = this.#getStoredObject();
		if (!storedObject) return null;
		return storedObject[key] as P;
	};

	/**
	 * Устанавливает значение параметра в хранилище
	 * @param part часть объекта хранилища
	 */
	set = (part: Partial<T>) => {
		const storedObject = this.#getStoredObject();
		const newObject = { ...storedObject, ...part };
		this.#setStoredObject(newObject);
	};

	/**
	 * Розумна установка значення для ключа об'єкта.
	 *
	 * Якщо значення непорожнє — воно зберігається.
	 * Якщо значення є null, undefined, false, пустим рядком, об'єктом `{}` або масивом `[]`,
	 * — відповідний ключ буде видалено з локального сховища.
	 *
	 * @template T - Тип об'єкта, з яким працює сховище.
	 * @param {keyof T} key - Ключ, який потрібно встановити або видалити.
	 * @param {any} value - Значення для встановлення або ознака для видалення.
	 */
	setSmart = (key: keyof T, value: any) => {
		const isEmptyObject = (val: any) => typeof val === 'object' && val !== null && Object.keys(val).length === 0;

		if (value && !isEmptyObject(value)) {
			this.set({ [key]: value } as Partial<T>);
		} else {
			const storedObject = this.#getStoredObject();
			if (!storedObject) return;
			const newObject = { ...storedObject };
			delete newObject[key];
			this.#setStoredObject(newObject);
		}
	};
}
